import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, ShieldCheck } from 'lucide-react';
import Logo from './Logo';

interface NotificationPromptProps {
  onAccept: () => void;
  onDecline: () => void;
  show: boolean;
  avoidBottomOverlap?: boolean;
}

export default function NotificationPrompt({ onAccept, onDecline, show, avoidBottomOverlap = false }: NotificationPromptProps) {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 100 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 100 }}
          className={`fixed ${avoidBottomOverlap ? 'bottom-[calc(env(safe-area-inset-bottom)+112px)]' : 'bottom-12'} md:bottom-12 left-6 right-6 md:left-auto md:right-12 md:max-w-md z-[200]`}
        >
          <div className="bg-white/80 backdrop-blur-2xl border border-brand-primary/5 rounded-[48px] p-10 shadow-[0_32px_128px_rgba(0,0,0,0.1)] relative overflow-hidden">
            {/* Decorative background logo */}
            <div className="absolute -right-8 -top-8 opacity-[0.02] scale-150 rotate-12 pointer-events-none">
              <Logo iconOnly />
            </div>

            <button 
              onClick={onDecline}
              className="absolute top-6 right-6 text-brand-primary/30 hover:text-brand-primary transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <X size={20} />
            </button>

            <div className="space-y-8 relative z-10">
              <div className="flex items-center gap-6">
                <div className="w-16 h-16 rounded-full bg-brand-accent/5 flex items-center justify-center border border-brand-accent/10">
                  <Bell size={24} className="text-brand-accent" />
                </div>
                <div className="space-y-1">
                  <span className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] text-brand-accent font-sans">Expérience</span>
                  <h3 className="text-3xl font-serif font-bold italic text-brand-primary">Rester en lien</h3>
                </div>
              </div>

              <p className="text-brand-primary/60 font-serif italic text-xl leading-relaxed">
                "Recevoir des conseils naturalistes et être averti en priorité des prochaines immersions sauvages."
              </p>

              <div className="flex flex-col gap-4 font-sans pt-4">
                <button 
                  onClick={onAccept}
                  className="bg-brand-primary text-white py-6 rounded-full text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] hover:bg-brand-accent transition-all duration-500 shadow-xl min-h-[48px]"
                >
                  Activer les notifications
                </button>
                <div className="flex items-center justify-center gap-3 text-[11px] md:text-xs font-bold uppercase tracking-[0.12em] opacity-55 text-brand-primary">
                  <ShieldCheck size={12} /> Respect de votre tranquillité
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
